import CrossSvg from "@icons/cross.svg?react";
import starSrc from "@icons/star.svg?url";
import starFilledSrc from "@icons/star-filled.svg?url";
import starOutlineSrc from "@icons/star-outline.svg?url";
import { msg, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import partition from "lodash-es/partition";
import { DateTime } from "luxon";
import React from "react";
import { Navigate, useParams } from "react-router-dom";

import { CropIcon } from "@/common/components/crop-icon/crop-icon";
import { Failed } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import { PageTitle } from "@/common/components/page-title/page-title";
import { PriceList } from "@/common/components/price-list/price-list";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { useCurrency } from "@/common/hooks/use-currency";
import { useErpCompany } from "@/common/hooks/use-erp-company";
import { useLanguage } from "@/common/hooks/use-language";
import {
  arrayEmptyCheckFn,
  useLocalStorage,
} from "@/common/hooks/use-local-storage";
import { useScrollbarToggle } from "@/common/hooks/use-scrollbar-toggle";
import { useToggle } from "@/common/hooks/use-toggle";
import { useCompany } from "@/common/providers/company-provider";
import { oneMinute } from "@/common/providers/query-provider";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { toggleItem } from "@/common/utils/array";
import { invariant } from "@/common/utils/invariant";
import { getPricesView } from "@/common/utils/trading";
import { FeatureMode } from "@/domain/plan/feature-mode.service";
import { PurchasePricesFlow } from "@/domain/plan/purchase-prices/purchase-prices.flow";
import { Currency } from "@/generated/digitalnisklady.cz/graphql";

import { TwoColumnLayout } from "../layout/layout";
import {
  cropPricesQuery,
  selectBestCropPrices,
  selectCropPrices,
} from "../shared/api/crop-prices.api";
import { usePostalCodesQuery } from "../shared/api/get-postal-codes";
import { LockedScreen } from "../shared/components/locked-screen/locked-screen";
import {
  PostalCodeDialog,
  usePostalCodeDialogParams,
} from "../shared/components/postal-code-dialog/postal-code-dialog";
import { PostalCodeInfoBox } from "../shared/components/postal-code-dialog/postal-code-info-box";
import { usePostalCodeObject } from "../shared/components/postal-code-dialog/use-postal-code-object";
import { IWantToTradeDialog } from "./components/i-want-to-trade-dialog";

const path = "purchase-prices";

const PurchasePrices = ({ mode }: { mode: FeatureMode }) => {
  const company = useErpCompany();
  const flow = React.useRef(new PurchasePricesFlow());
  flow.current.setStrategy(mode);
  const { marketPriceId } = useParams();
  const isDetailOpen = marketPriceId !== undefined;
  const sidebar = useSidebar();
  const isRightPaneOpen = isDetailOpen || sidebar.state.screen;
  const { _ } = useLingui();
  const { params } = usePostalCodeDialogParams();
  useScrollbarToggle(params?.enforceDialog);
  const [CurrencySwitch, currency] = useCurrency();
  const postalCodes = useQuery(usePostalCodesQuery());
  const postalCodeDialog = usePostalCodeDialogParams();
  const postalCodeObject = usePostalCodeObject(
    postalCodeDialog.params?.postalCode,
  );
  const [favorites, setFavorites] = useFavorites(flow.current.getFavorites());
  const showPackages = useToggle();
  const language = useLanguage();

  const cropPrices = useQuery({
    refetchInterval: oneMinute,
    ...cropPricesQuery({
      companyId: company?.id,
      currency: currency as Currency,
      // @ts-expect-error this can hardly ever be undefined, yet I have no idea how to express it here
      latitude: postalCodeObject.latitude,
      locale: language,
      // @ts-expect-error this can hardly ever be undefined, yet I have no idea how to express it here
      longitude: postalCodeObject.longitude,
    }),
    enabled: !!postalCodeObject.latitude,
    select: (data) => selectBestCropPrices(selectCropPrices(data)),
  });
  const [favoriteCropPrices, otherCropPrices] = partition(
    cropPrices.data,
    (price) => favorites.includes(price.crop.name),
  );

  const toggleFavorite = (cropName: string) => {
    setFavorites((current) => (current ? toggleItem(current, cropName) : []));
  };

  if (postalCodes.status === "success" && params?.postalCode) {
    if (
      !postalCodes.data.find(
        (postalCode) => postalCode.code === params.postalCode,
      )
    ) {
      return <Navigate to="?" />;
    }
  }

  if (showPackages.on) {
    return (
      <LockedScreen>
        <button className="mt-2 mr-2 self-start" onClick={showPackages.setOff}>
          <CrossSvg />
        </button>
      </LockedScreen>
    );
  }

  return (
    <TwoColumnLayout
      left={{
        content: (
          <>
            <div className="flex basis-full flex-col">
              <Metadata title={_(msg`Moje výkupní ceny`)} />
              <Spacer />
              <PostalCodeInfoBox
                code={postalCodeObject.code}
                name={postalCodeObject.name}
                onPostalCodeChange={postalCodeDialog.enforceDialog}
              >
                <Trans>
                  Nyní zobrazujeme aktuální ceny komodit pro oblast dle
                  poštovního směrovacího čísla (PSČ)
                </Trans>
              </PostalCodeInfoBox>

              <Spacer className="h-6" />

              {cropPrices.status === "pending" ? (
                <div className="flex basis-full items-center justify-center">
                  <Spinner />
                </div>
              ) : null}
              {cropPrices.status === "error" ? (
                <Failed error={cropPrices.error} />
              ) : null}
              {cropPrices.status === "success" ? (
                <div className="flex flex-col gap-y-8">
                  {[
                    {
                      icon: starFilledSrc,
                      iconTitle: t`Odebrat z oblíbených`,
                      items: favoriteCropPrices,
                      title: (
                        <span
                          className="flex items-center gap-2"
                          title="Zaškrtnutím hvězdičky přidáte komoditu do oblíbených"
                        >
                          <img src={starOutlineSrc} />{" "}
                          <Trans>Moje oblíbené komodity</Trans>
                        </span>
                      ),
                      titleTooltip: t`Zaškrtnutím hvězdičky u jednotlivých komodit je přesunete v seznamu výše`,
                    },
                    {
                      icon: starSrc,
                      iconTitle: t`Přidat do oblíbených`,
                      items: otherCropPrices,
                      title: t`Ostatní komodity`,
                    },
                  ].map((section) => (
                    <section
                      className="flex flex-col gap-4"
                      key={section.iconTitle}
                    >
                      <h2
                        className="text-can-forest-teal flex items-center gap-2 font-bold"
                        title={section.titleTooltip}
                      >
                        {section.title}
                      </h2>

                      <div
                        className={`grid w-full gap-6 ${isRightPaneOpen ? "grid-cols-1 2xl:grid-cols-2" : "grid-cols-1 xl:grid-cols-3"}`}
                      >
                        {section.items.map((item) => (
                          <section
                            className="shadow-can-light-box flex flex-col gap-y-2 rounded-2xl p-4"
                            key={item.crop.name}
                          >
                            <h2 className="text-can-midnight-steel flex items-center gap-x-2 font-bold">
                              <CropIcon cropName={item.crop.name} />{" "}
                              {item.crop.name}
                              <button
                                aria-label={section.iconTitle}
                                className="group hover:bg-can-silver-gray -mr-2 ml-auto flex content-center rounded-full p-2"
                                onClick={() => toggleFavorite(item.crop.name)}
                                title={section.iconTitle}
                              >
                                <img
                                  className="group-hover:hidden"
                                  src={section.icon}
                                />
                                <img
                                  className="hidden group-hover:inline"
                                  src={starOutlineSrc}
                                />
                              </button>
                            </h2>
                            {getPricesView(item.prices).map((prices, i) => (
                              <PriceList
                                className={`${flow.current.getMasked(item.crop.name) ? "[&_li]:blur-xs" : ""} ${i === 0 && prices.length === 1 ? "[&>li]:col-start-4 [&>li]:border-r-0 [&>li]:border-l" : ""}`}
                                key={i}
                                prices={prices.map((p) => {
                                  const masked = flow.current.getMaskedPrice(
                                    item.crop.name,
                                    currency,
                                  );
                                  if (masked) {
                                    return masked;
                                  }
                                  return p?.type === "data"
                                    ? {
                                        currency,
                                        id: p.id,
                                        quarter: p.quarter,
                                        value: p.price,
                                      }
                                    : null;
                                })}
                              />
                            ))}
                          </section>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : null}
              <Spacer />
              {cropPrices.dataUpdatedAt > 0 ? (
                <p className="mt-auto mb-8 text-center text-sm">
                  <Trans>Aktualizováno</Trans>{" "}
                  {DateTime.fromMillis(cropPrices.dataUpdatedAt).toLocaleString(
                    DateTime.DATETIME_FULL,
                  )}
                </p>
              ) : null}
              <Spacer />
            </div>
            {params?.enforceDialog ? (
              <PostalCodeDialog>
                <Trans>
                  Pro výpočet ceny vyplňte do pole níže poštovní směrovací číslo
                  umístění skladu (PSČ).
                </Trans>
              </PostalCodeDialog>
            ) : (
              <IWantToTradeDialog onClick={showPackages.setOn} />
            )}
          </>
        ),
        header: (
          <>
            <PageTitle>
              <Trans>Moje výkupní ceny</Trans>
            </PageTitle>
            {CurrencySwitch}
          </>
        ),
      }}
      right={isDetailOpen}
    ></TwoColumnLayout>
  );
};

const useFavorites = (initialValue?: string[]) => {
  const { company: activeCompany } = useCompany();
  invariant(activeCompany);
  const [favorites, setFavorites] = useLocalStorage<string[]>(
    "purchase-prices.favorites",
    initialValue,
    arrayEmptyCheckFn,
  );

  return [
    favorites as Exclude<typeof favorites, undefined>,
    setFavorites,
  ] as const;
};

export { getPricesView, path, PurchasePrices };
